import * as React from "react";
import { Router, RouteProps, Match, Params, RouterState } from "found";
import { GraphQLTaggedNode, QueryRenderer, ReadyState } from "react-relay";
import { getCurrentEnvironment } from "@state/relay-environment";

// eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-unsafe-assignment
const StaticContainer = require("react-static-container");

const PathNameSettingsDrawer = "/settings";

const SearchParamDefragPreview = "preview";
const SearchParamSettingsDefrag = "optimizer";
// const SearchParamChromeMismatch = 'mismatch';

//////////////////
// ROUTE CONFIG
//////////////////

export const noopComponent = StaticContainer as React.ComponentType<any>;

export function getComponentOrRedirect<T>(
  component: React.ComponentType<T>,
  requirement: (props: RouteProps) => string | undefined,
  props: RouteProps,
): React.ComponentType<T> {
  const redirect = requirement(props);
  if (redirect) {
    props.router.replace(redirect);
    return noopComponent;
  }

  return component;
}

//////////////////////
// URL PATH NAME
//////////////////////

export function existsInPathName(resolvedMatch: Match, pathName: string) {
  return resolvedMatch.location.pathname.indexOf(pathName) > -1;
}

export function addToCalPath(router: Router, resolvedMatch: Match, pathName: string) {
  if (!resolvedMatch.location.pathname.endsWith(pathName)) {
    let url = "/cal/" + resolvedMatch.params.calDateRange + pathName;
    const searchParams = getSearchParams(resolvedMatch);
    if (searchParams.toString().length) {
      url += "?" + searchParams.toString();
    }
    router.replace(url);
  }
}

export function removeEndPathName(router: Router, resolvedMatch: Match, pathName: string) {
  if (resolvedMatch.location.pathname.endsWith(pathName)) {
    const index = resolvedMatch.location.pathname.lastIndexOf(pathName);
    let url = resolvedMatch.location.pathname.substr(0, index);
    if (resolvedMatch.location.search) {
      url += resolvedMatch.location.search;
    }
    router.replace(url);
  }
}

export function replaceCalDateRangeInCalPath(
  router: Router,
  resolvedMatch: Match,
  calDateRange: string,
) {
  const parts = resolvedMatch.location.pathname.replace(/\/$/, "").split("/");
  let url = "/cal/" + calDateRange;
  if (parts.length > 3) {
    url += "/" + parts.slice(3, parts.length).join("/");
  }
  const searchParams = getSearchParams(resolvedMatch);
  if (searchParams.toString().length) {
    url += "?" + searchParams.toString();
  }
  router.replace(url);
}

//////////////////////
// URL SEARCH PARAMS
//////////////////////

export function getSearchParams(resolvedMatch: Match) {
  const search = resolvedMatch.location.search;
  return new URLSearchParams(search);
}

export function existsInSearchParams(resolvedMatch: Match, searchParam: string) {
  const searchParams = getSearchParams(resolvedMatch);
  return searchParams.has(searchParam);
}

export function addSearchParam(router: Router, resolvedMatch: Match, searchParam: string) {
  const searchParams = getSearchParams(resolvedMatch);
  if (!searchParams.has(searchParam)) {
    searchParams.append(searchParam, "1");
    // use window.location.pathname as we may be doing multiple
    router.replace(window.location.pathname + "?" + searchParams.toString());
  }
}

export function removeSearchParam(router: Router, resolvedMatch: Match, searchParam: string) {
  const searchParams = getSearchParams(resolvedMatch);
  if (searchParams.has(searchParam)) {
    searchParams.delete(searchParam);
    let url = resolvedMatch.location.pathname;
    if (searchParams.toString().length) {
      url += "?" + searchParams.toString();
    }
    router.replace(url);
  }
}

//////////////////
// DEFRAG PREVIEW
//////////////////

export function isDefragPreviewOnFromRoute(resolvedMatch: Match) {
  return existsInSearchParams(resolvedMatch, SearchParamDefragPreview);
}

export function removeDefragPreviewFromRoute(router: Router, resolvedMatch: Match) {
  removeSearchParam(router, resolvedMatch, SearchParamDefragPreview);
}

export function addDefragPreviewToRoute(router: Router, resolvedMatch: Match) {
  addSearchParam(router, resolvedMatch, SearchParamDefragPreview);
}

//////////////////////
// SETTINGS DRAWER
//////////////////////

export function isSettingsDrawerOnFromRoute(resolvedMatch: Match) {
  return existsInPathName(resolvedMatch, PathNameSettingsDrawer);
}

export function removeSettingsDrawerFromRoute(router: Router, resolvedMatch: Match) {
  removeEndPathName(router, resolvedMatch, PathNameSettingsDrawer);
}

export function addSettingsDrawerToRoute(router: Router, resolvedMatch: Match) {
  addToCalPath(router, resolvedMatch, PathNameSettingsDrawer);
}

///////////////////////////////
// SETTINGS DRAWER WITH DEFRAG
///////////////////////////////

export function isSettingsDefragOnFromRoute(resolvedMatch: Match) {
  return (
    existsInPathName(resolvedMatch, PathNameSettingsDrawer) &&
    existsInSearchParams(resolvedMatch, SearchParamSettingsDefrag)
  );
}

export function removeSettingsDefragFromRoute(router: Router, resolvedMatch: Match) {
  removeSearchParam(router, resolvedMatch, SearchParamSettingsDefrag);
}

export function addSettingsDefragToRoute(router: Router, resolvedMatch: Match) {
  addSettingsDrawerToRoute(router, resolvedMatch);
  addSearchParam(router, resolvedMatch, SearchParamSettingsDefrag);
}

////////////////////////////////////////////////////
// TEMP ROUTE BANDAID FOR DEPRECATING FOUND RELAY //
////////////////////////////////////////////////////

interface RouteQueryOptions {
  prepareVariables?: (params: Params) => object;
}

export const withRouteQuery = <P extends React.PropsWithChildren<RouterState>>(
  ComponentClass: any,
  query: GraphQLTaggedNode | null | undefined,
  { prepareVariables }: RouteQueryOptions = {},
) =>
  function QueryRendererContainer(props: P) {
    const environment = getCurrentEnvironment();

    return (
      <QueryRenderer
        environment={environment}
        query={query}
        variables={prepareVariables ? prepareVariables(props.match.params) : {}}
        render={(readyState: ReadyState) => {
          // check for error or show a loader for first load
          if (!Object.keys(readyState.props ?? {}).length) {
            return null;
          }

          return <ComponentClass {...props} {...readyState.props} />;
        }}
      />
    );
  };
